import React, { createContext, useContext, useRef, useState, useCallback, useMemo } from "react";

export interface ComboxContextState {
  // Open/close state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Search functionality
  searchQuery: string;
  onSearchChange: (query: string) => void;

  // Highlighting for keyboard navigation
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  setHighlightedIndexWithMethod: (index: number, method: "keyboard" | "mouse") => void;
  highlightMethod: "keyboard" | "mouse" | null;

  // Refs for focus management
  searchInputRef: React.RefObject<HTMLInputElement>;
  contentRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLButtonElement>;

  // Value management - single select
  selectedValue?: string;
  onValueChange?: (value: string) => void;

  // Value management - multi select
  selectedValues: string[];
  onValuesChange?: (values: string[]) => void;

  // Config
  disabled?: boolean;
  multiple?: boolean;
}

export interface ComboxContextProps {
  children: React.ReactNode;

  // Single-select value props
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;

  // Multi-select value props
  values?: string[];
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;

  // Open state props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Search callback
  onSearchChange?: (query: string) => void;

  // Config
  disabled?: boolean;
  multiple?: boolean;
  closeOnSelect?: boolean;
}

const ComboxContext = createContext<ComboxContextState | null>(null);

export const ComboxProvider = ({
  children,
  value: controlledValue,
  defaultValue,
  onValueChange,
  values: controlledValues,
  defaultValues,
  onValuesChange,
  open: controlledOpen,
  onOpenChange,
  onSearchChange,
  disabled = false,
  multiple = false,
  closeOnSelect = true,
}: ComboxContextProps) => {
  // Internal state
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [internalValues, setInternalValues] = useState(defaultValues || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [highlightMethod, setHighlightMethod] = useState<"keyboard" | "mouse" | null>(null);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Use controlled or internal state
  const open = controlledOpen ?? internalOpen;
  const selectedValue = controlledValue ?? internalValue;
  const selectedValues = controlledValues ?? internalValues;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (controlledOpen !== undefined) {
        onOpenChange?.(newOpen);
      } else {
        setInternalOpen(newOpen);
      }

      if (!newOpen) {
        setHighlightedIndex(-1);
        setHighlightMethod(null);
        setSearchQuery("");
      }
    },
    [controlledOpen, onOpenChange],
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (multiple) {
        // Multi-select mode: toggle value in array
        const newValues = selectedValues.includes(newValue)
          ? selectedValues.filter((value) => value !== newValue)
          : [...selectedValues, newValue];

        if (controlledValues === undefined) {
          setInternalValues(newValues);
        }
        onValuesChange?.(newValues);
        // Don't close on multi-select
      } else {
        // Single-select mode: set single value
        if (controlledValue === undefined) {
          setInternalValue(newValue);
        }
        onValueChange?.(newValue);
        if (closeOnSelect) {
          handleOpenChange(false);
        }
      }
    },
    [
      multiple,
      selectedValues,
      controlledValues,
      controlledValue,
      onValuesChange,
      onValueChange,
      handleOpenChange,
      closeOnSelect,
    ],
  );

  const handleValuesChange = useCallback(
    (newValues: string[]) => {
      if (controlledValues === undefined) {
        setInternalValues(newValues);
      }
      onValuesChange?.(newValues);
    },
    [controlledValues, onValuesChange],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setHighlightedIndex(-1);
      setHighlightMethod(null);
      onSearchChange?.(query);
    },
    [onSearchChange],
  );

  const setHighlightedIndexWithMethod = useCallback(
    (index: number, method: "keyboard" | "mouse") => {
      setHighlightedIndex(index);
      setHighlightMethod(method);
    },
    [],
  );

  const contextValue: ComboxContextState = useMemo(
    () => ({
      open,
      setOpen: handleOpenChange,
      searchQuery,
      onSearchChange: handleSearchChange,
      highlightedIndex,
      setHighlightedIndex,
      setHighlightedIndexWithMethod,
      highlightMethod,
      searchInputRef,
      contentRef,
      triggerRef,
      selectedValue,
      onValueChange: handleValueChange,
      selectedValues,
      onValuesChange: handleValuesChange,
      disabled,
      multiple,
    }),
    [
      open,
      handleOpenChange,
      searchQuery,
      handleSearchChange,
      highlightedIndex,
      setHighlightedIndex,
      setHighlightedIndexWithMethod,
      highlightMethod,
      searchInputRef,
      contentRef,
      triggerRef,
      selectedValue,
      handleValueChange,
      selectedValues,
      handleValuesChange,
      disabled,
      multiple,
    ],
  );

  return <ComboxContext.Provider value={contextValue}>{children}</ComboxContext.Provider>;
};

export const useComboxContext = () => {
  const context = useContext(ComboxContext);
  if (!context) {
    throw new Error("useComboxContext must be used within a ComboxProvider");
  }
  return context;
};
